// components/FeaturedRooms.jsx
import React, { useEffect, useRef, useState } from 'react';
import { 
  FaBed, 
  FaUsers, 
  FaStar, 
  FaArrowRight, 
  FaWifi, 
  FaCoffee, 
  FaTv,
  FaWind,
  FaBath,
  FaPercent,
  FaCalendarAlt,
  FaPhoneAlt
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const FeaturedRooms = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeOffer, setActiveOffer] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveOffer((prev) => (prev + 1) % offers.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

const featuredRooms = [
  {
    id: 1,
    name: 'Single Room',
    type: 'Non AC',
    price: 799,
    duration: '1 Day',
    guests: 1,
    bed: 'Single Bed',
    size: '180 sq ft',
    image: 'image1.png',
    amenities: ['WiFi', 'TV']
  },
  {
    id: 2,
    name: 'Double Room',
    type: 'Non AC',
    price: 1199,
    duration: '1 Day',
    guests: 2,
    bed: 'Double Bed',
    size: '220 sq ft',
    image: 'image2.png',
    amenities: ['WiFi', 'TV']
  },
  {
    id: 3,
    name: 'Single Room',
    type: 'AC',
    price: 899,
    duration: '1 Day',
    guests: 1,
    bed: 'Single Bed',
    size: '200 sq ft',
    image: 'image3.png',
    amenities: ['WiFi', 'AC', 'TV']
  },
  {
    id: 4,
    name: 'Double Room',
    type: 'AC',
    price: 1299,
    duration: '1 Day',
    guests: 2,
    bed: 'King Bed',
    size: '300 sq ft',
    image: 'image4.png',
    amenities: ['WiFi', 'AC', 'TV']
  },
];

  const offers = [
    {
      title: 'Early Bird Offer',
      discount: '20%',
      code: 'EARLY20',
      description: 'Book 30 days in advance and get 20% off',
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Weekend Special',
      discount: '15%',
      code: 'WEEKEND15',
      description: 'Enjoy 15% off on Friday & Saturday stays',
      color: 'from-orange-500 to-red-500'
    },
    {
      title: 'Long Stay Deal',
      discount: '25%',
      code: 'STAY25',
      description: 'Book 5+ nights and save 25%',
      color: 'from-purple-500 to-pink-500'
    },
  ];

  return (
    <section ref={sectionRef} className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        
        {/* Section Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider bg-blue-50 px-4 py-1.5 rounded-full inline-block mb-3">
            Featured Rooms
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
            Popular <span className="text-blue-600">Choices</span>
          </h2>
          <div className="w-20 h-1 bg-blue-500 mx-auto rounded-full"></div>
          <p className="text-gray-600 max-w-2xl mx-auto mt-4">
            Our most loved rooms, curated for your comfort and luxury
          </p>
        </div>

        {/* Rooms Grid - SINGLE ROW (4 columns on large screens) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {featuredRooms.map((room, index) => (
            <div
              key={room.id}
              className={`bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group hover:-translate-y-2 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Image Container */}
              <div className="relative overflow-hidden h-48">
                <img 
                  src={room.image} 
                  alt={room.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                {room.discount && (
                  <div className="absolute top-3 left-3 bg-orange-500 text-white px-2 py-1 rounded-lg text-xs font-bold flex items-center gap-1">
                    <FaPercent size={10} />
                    {room.discount}% OFF
                  </div>
                )}
                {room.popular && (
                  <div className="absolute top-3 right-3 bg-blue-500 text-white px-2 py-1 rounded-lg text-xs font-bold flex items-center gap-1">
                    <FaStar size={10} />
                    Popular
                  </div>
                )}
                {/* AC/Non AC Badge */}
                <div className="absolute bottom-3 left-3">
                  {room.type === 'AC' ? (
                    <span className="text-xs font-semibold text-blue-600 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full">
                      ❄️ AC
                    </span>
                  ) : (
                    <span className="text-xs font-semibold text-orange-600 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full">
                      🔥 Non AC
                    </span>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">{room.name}</h3>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400 line-through">₹{room.originalPrice}</p>
                    <p className="text-xl font-bold text-orange-600">₹{room.price}</p>
                    <p className="text-xs text-gray-500">/night</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                  <span className="flex items-center gap-1">
                    <FaBed size={11} />
                    {room.bed}
                  </span>
                  <span className="flex items-center gap-1">
                    <FaUsers size={11} />
                    {room.guests}
                  </span>
                </div>

                <p className="text-gray-500 text-xs mb-3">
                  {room.size}
                </p>

                {/* Amenities */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {room.amenities.map((item, i) => (
                    <span key={i} className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                      {item === 'WiFi' && <FaWifi className="text-blue-500" size={9} />}
                      {item === 'AC' && <FaWind className="text-blue-500" size={9} />}
                      {item === 'TV' && <FaTv className="text-blue-500" size={9} />}
                      {item === 'Mini Bar' && <FaCoffee className="text-blue-500" size={9} />}
                      {item}
                    </span>
                  ))}
                </div>

                {/* Book Now Button */}
                <button
                  onClick={() => {
                    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full bg-gradient-to-r from-orange-500 to-blue-600 text-white py-2 rounded-lg text-sm font-semibold text-center flex items-center justify-center gap-2 transition-all hover:scale-105"
                >
                  Book Now
                  <FaArrowRight size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action Banner */}
        <div className={`bg-blue-600 rounded-2xl p-8 text-center text-white transition-all duration-700 delay-700 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="text-left">
              <h3 className="text-2xl font-bold mb-2">Need Help Choosing?</h3>
              <p className="text-blue-100">Our team is here to help you find the perfect room</p>
            </div>
            <div className="flex flex-wrap justify-center md:justify-end gap-4">
              <a
                href="tel:+919555040589"
                className="flex items-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-all"
              >
                <FaPhoneAlt />
                Call Now
              </a>
              <button
                onClick={() => {
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex items-center gap-2 border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-all"
              >
                <FaCalendarAlt />
                Inquire Now
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Note */}
        <div className={`text-center mt-8 transition-all duration-700 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="text-xs text-gray-400">
            *Prices are per night and include taxes. Terms and conditions apply.
          </p>
        </div>
      </div>
    </section>
  );
};

export default FeaturedRooms;